import { useEffect, useRef, useState } from 'react';
import { Box, Button, Collapse, Paper, Typography } from '@mui/material';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-toastify';
import api from '../../utils/axiosConfig';
import { links, runtime } from '../../platform';
import { dashboardPanelSx } from '../Dashboard/DeviceStatusPanel';

export default function DeviceNumbers({ unit, numbers = {} }) {
    const [showPhoneNumbers, setShowPhoneNumbers] = useState(false);
    const [isCreatingContact, setIsCreatingContact] = useState(false);
    const pending = useRef(null);
    useEffect(() => () => pending.current?.abort(), []);
    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return 'Not available';
        
        // Remove all non-digits
        const digits = String(phoneNumber).replace(/\D/g, '');
        
        // Format as (XXX) XXX-XXXX
        if (digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        } else if (digits.length === 11 && digits[0] === '1') {
            return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
        }
        
        // Return as-is if format doesn't match
        return String(phoneNumber);
    };

    const handleAddToContacts = async () => {
        const customerName = [unit?.FirstName, unit?.LastName]
            .filter(Boolean)
            .join(' ') || unit?.CustomerAlias || 'Device User';

        if (pending.current) return;
        const controller = new AbortController();
        pending.current = controller;
        setIsCreatingContact(true);
        try {
            const native = runtime.isNative();
            const response = await api.post(native ? '/api/contacts/device.vcf' : '/api/contacts/device-link', {
                customerName,
                callDevice: numbers?.unitPhoneNumber,
                incomingDeviceCalls: numbers?.receiveCallsFrom,
                textDevice: numbers?.smsPhoneNumber
            }, { signal: controller.signal, ...(native ? { responseType: 'text' } : {}) });
            if (controller.signal.aborted) return;
            if (native) await links.openContactFile(response.data, { signal: controller.signal });
            else await links.openContact(response.data.url);
        } catch (error) {
            if (controller.signal.aborted) return;
            toast.error('Unable to open the contact card. Please try again.');
        } finally {
            if (!controller.signal.aborted) {
                pending.current = null;
                setIsCreatingContact(false);
            }
        }
    };

    return (
        <Paper variant="outlined" sx={dashboardPanelSx}>
            <Button fullWidth color="inherit" onClick={() => setShowPhoneNumbers(!showPhoneNumbers)}
                aria-expanded={showPhoneNumbers} aria-controls="device-contact-details"
                startIcon={<ContactPhoneIcon />} endIcon={showPhoneNumbers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ minHeight: 64, px: 2, justifyContent: 'flex-start', textTransform: 'none', textAlign: 'left', fontSize: '1rem', fontWeight: 650, '& .MuiButton-endIcon': { ml: 'auto', pl: 1 } }}>
                Numbers to Remember
            </Button>
            <Collapse in={showPhoneNumbers} id="device-contact-details">
                <Box sx={{ px: 2, pt: 1, pb: 2, overflowWrap: 'anywhere' }}>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        <strong>The number you'll get calls from:</strong>
                        <br />
                        {formatPhoneNumber(numbers.receiveCallsFrom)}
                        <br />
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        <strong>The number you need to call to reach the device:</strong>
                        <br />
                        {formatPhoneNumber(numbers.unitPhoneNumber)}
                        <br />
                    </Typography>


                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        <strong>The number you need to call from:</strong>
                        <br />
                        {formatPhoneNumber(numbers.userPhoneNumber)}
                        <br />
                    </Typography>

                    <Typography variant="body2" component="div">
                        <strong>Text communication & location requests:</strong>
                        <ul>
                            <li>Text "Location" to <strong>{formatPhoneNumber(numbers.smsPhoneNumber)}</strong> to receive a real-time Google Maps link showing the device's last known location.  </li>
                            <li>
                                You will also receive text notifications at this number when the help button is pressed.
                            </li>
                        </ul>
                    </Typography>
                    <Button
                        onClick={handleAddToContacts}
                        disabled={isCreatingContact}
                        variant="outlined"
                        startIcon={<ContactPhoneIcon />}
                        sx={{ mt: 0.5, minHeight: 48 }}
                    >
                        {isCreatingContact ? 'Preparing Contact...' : 'Open Contact Card'}
                    </Button>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1 }}
                    >
                        This opens a contact card but does not save it automatically.
                        <br />
                        <strong>iPhone:</strong> {runtime.isNative() ? 'Review the contact, then tap Done to save it.' : 'Tap Create New Contact, then Done.'}
                        <br />
                        <strong>Android:</strong> {runtime.isNative() ? 'Choose Contacts and an account if asked, then confirm the import.' : 'Open the downloaded .vcf file, choose an account if asked, then tap Import. If it does not open, use Contacts > Organize > Import from file.'}
                    </Typography>
                </Box>
            </Collapse>
        </Paper>
    );
}
